import React from 'react';
import { Box, Typography } from '@mui/material';
import { useElapsedTime } from '../hooks/useElapsedTime';

interface Vyjezd2026CasovkaOverlayProps {
    casVyhlaseni: string;
    casVyjezdMinuty: number;  // povinny - zadny interni query (K0.5)
    /** 'overlay' = překryv mapy na šířku (výchozí), 'inline' = v hlavičce kiosku na výšku. */
    varianta?: 'overlay' | 'inline';
    /** Jen inline: písmo času v px; omezí se na CASOVKA_INLINE_MIN_PX..CASOVKA_INLINE_MAX_PX. */
    velikostPx?: number;
}

export const CASOVKA_INLINE_MIN_PX = 40;
export const CASOVKA_INLINE_MAX_PX = 88;

/** Padding hlavičky na výšku nad a pod obsahem (12 + 12 px), viz Vyjezd2026Hlavicka. */
const PADDING_HLAVICKY_PX = 24;
/** Inline časovka kromě času: popisek 1,25rem × 1,2 = 24 px + py 2 × 12 px. */
const REZIE_INLINE_CASOVKY_PX = 48;

/**
 * Písmo času inline časovky: 38 % výšky hlavičky (spec kap. 3.2), ale jen tolik, aby se celá
 * časovka vešla do výšky hlavičky po odečtení banneru vzorového poplachu a paddingu. 40–88 px.
 */
export const velikostCasovkyPx = (vyskaHlavicky: number, odsazeniShoraPx: number): number => {
    const dostupne = vyskaHlavicky - odsazeniShoraPx - PADDING_HLAVICKY_PX;
    const podleMista = Math.floor((dostupne - REZIE_INLINE_CASOVKY_PX) / 1.05);
    const px = Math.min(Math.round(vyskaHlavicky * 0.38), podleMista);
    return Math.min(CASOVKA_INLINE_MAX_PX, Math.max(CASOVKA_INLINE_MIN_PX, px));
};

export const Vyjezd2026CasovkaOverlay: React.FC<Vyjezd2026CasovkaOverlayProps> = ({
    casVyhlaseni, casVyjezdMinuty, varianta = 'overlay', velikostPx = CASOVKA_INLINE_MAX_PX,
}) => {
    const { elapsed, formatted, shouldBlink } = useElapsedTime(casVyhlaseni, casVyjezdMinuty);

    if (varianta === 'inline') {
        const px = Math.min(CASOVKA_INLINE_MAX_PX, Math.max(CASOVKA_INLINE_MIN_PX, Math.round(velikostPx)));
        return (
            <Box data-testid="casovka-inline" sx={{
                flex: 'none', bgcolor: 'rgba(0, 0, 0, 0.82)', color: '#ffffff',
                borderRadius: 2, px: 3, py: 1.5, textAlign: 'center',
                ...(shouldBlink && {
                    animation: 'blink 1s ease-in-out infinite',
                    '@keyframes blink': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.3 } },
                }),
            }}>
                <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, letterSpacing: '1px', color: '#cfcfcf', lineHeight: 1.2 }}>
                    OD VYHLÁŠENÍ
                </Typography>
                <Typography data-testid="casovka-cas" sx={{
                    fontWeight: 'bold', fontSize: `${px}px`, lineHeight: 1.05,
                    color: elapsed < 0 ? '#7CFC00' : '#FF6B6B',
                    fontVariantNumeric: 'tabular-nums', fontFeatureSettings: '"tnum"', fontFamily: 'monospace',
                }}>
                    {formatted}
                </Typography>
            </Box>
        );
    }

    return (
        <Box
            data-testid="casovka-overlay"
            sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                zIndex: 1000,
                bgcolor: 'rgba(0, 0, 0, 0.65)',
                color: '#ffffff',
                borderRadius: 2,
                px: 2,
                py: 1,
                ...(shouldBlink && {
                    animation: 'blink 1s ease-in-out infinite',
                    '@keyframes blink': {
                        '0%, 100%': { opacity: 1 },
                        '50%': { opacity: 0.3 },
                    },
                }),
            }}
        >
            <Typography
                sx={{
                    fontWeight: 'bold',
                    fontSize: 'var(--dashboard-vyjezd-time-size, 4rem)',
                    lineHeight: 1,
                    color: elapsed < 0 ? '#7CFC00' : '#FF6B6B',
                    // Tabular numerals — všechny číslice mají stejnou šířku, takže layout
                    // neposkakuje s každou změnou sekundy. Bez tohoto se "-" sign a kratší
                    // číslice (1) působí jako nesymetrický odsazení proti pravému okraji.
                    fontVariantNumeric: 'tabular-nums',
                    fontFeatureSettings: '"tnum"',
                    textAlign: 'center',
                    fontFamily: 'monospace',
                }}
            >
                {formatted}
            </Typography>
        </Box>
    );
};
